/* eslint-disable no-undef, no-console */
import { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error('useAuth must be used within an AuthProvider');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  console.log('AuthProvider initializing...');
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [accountStatus, setAccountStatus] = useState(null); // Track deactivation status
  const navigate = useNavigate();
  const location = useLocation();

  // Logout the user - defined early to avoid circular dependency
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('Tasklina-current-user');
    setToken(null);
    setCurrentUser(null);
    setAccountStatus(null);
    navigate('/auth/login');
  };

  // Check if token is valid
  const isTokenValid = (token) => {
    if (!token) return false;

    // Check if it's a mock token (for development)
    if (token.startsWith('mock-token-')) {
      return true;
    }

    try {
      const decoded = jwtDecode(token);
      return decoded.exp > Date.now() / 1000;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      console.log('AuthContext initAuth:', { token, isValid: token ? isTokenValid(token) : false });
      
      if (token && isTokenValid(token)) {
        try {
          console.log('Getting current user...');
          const response = await authService.getCurrentUser();
          console.log('Current user response:', response);
          
          // Handle both old format (response is user) and new format (response has accountStatus)
          const userData = response.data || response;
          setCurrentUser(userData);
          
          // Check for account status (deactivation info)
          if (response.accountStatus?.isDeactivated) {
            setAccountStatus(response.accountStatus);
            // Redirect to deactivated page if not already there
            if (!location.pathname.startsWith('/account-deactivated')) {
              navigate('/account-deactivated');
            }
          }
        } catch (error) {
          console.error('Error getting current user:', error);
          // Check if it's a deactivation error
          if (error.response?.data?.code === 'ACCOUNT_DEACTIVATED') {
            setAccountStatus({
              isDeactivated: true,
              ...error.response.data.deactivationInfo
            });
            navigate('/account-deactivated');
          } else {
            logout();
          }
        }
      } else if (token) {
        // Token exists but is invalid
        console.log('Token is invalid, logging out');
        logout();
      }
      
      setLoading(false);
    };

    initAuth();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Login the user
  const login = async (email, password) => {
    console.log('AuthContext login called:', { email, password });
    
    try {
      const response = await authService.login(email, password);
      console.log('AuthService response:', response);
      
      const { token, user } = response;
      
      localStorage.setItem('token', token);
      setToken(token);
      setCurrentUser(user);
      
      console.log('Login successful, user set:', user);
      return user;
    } catch (error) {
      console.error('Login error in AuthContext:', error);
      throw error;
    }
  };

  // Social login
  const socialLogin = async (provider, token) => {
    try {
      const response = await authService.socialLogin(provider, token);
      const { token: authToken, user } = response;
      
      localStorage.setItem('token', authToken);
      setToken(authToken);
      setCurrentUser(user);
      
      return user;
    } catch (error) {
      throw new Error(error.response?.data?.error || `${provider} login failed`);
    }
  };

  // Register a new user (legacy direct register — still works)
  const register = async (userData) => {
    const response = await authService.register(userData);
    const { token, user } = response;
    
    localStorage.setItem('token', token);
    setToken(token);
    setCurrentUser(user);
    
    return user;
  };

  // Step 1: Send verification code for registration
  const sendVerificationCode = async (userData) => {
    return await authService.sendVerificationCode(userData);
  };

  // Step 2: Verify code and complete registration
  const verifyAndRegister = async ({ email, code }) => {
    const response = await authService.verifyAndRegister({ email, code });
    const { token, user } = response;
    
    localStorage.setItem('token', token);
    setToken(token);
    setCurrentUser(user);
    
    return user;
  };

  // Resend verification code
  const resendVerificationCode = async (email) => {
    return await authService.resendVerificationCode(email);
  };


  // Check if user has a specific role
  // UPDATED: Prioritize platformRole, map legacy 'admin' to platform roles
  const hasRole = (roles) => {
    if (!currentUser) return false;
    
    const platformRole = currentUser.platformRole;
    const orgRole = currentUser.orgRole;
    
    // Handle legacy 'admin' role check - map to platform_admin/super_admin
    if (roles.includes('admin')) {
      if (['platform_admin', 'super_admin'].includes(platformRole)) {
        return true;
      }
    }
    
    // Check platform role (primary for platform-level access)
    if (platformRole && roles.includes(platformRole)) return true;
    
    // Check organization role
    if (orgRole && roles.includes(orgRole)) return true;
    
    // Check for basic 'user' role - all authenticated users qualify
    if (roles.includes('user')) return true;
    
    return false;
  };

  // Check if user has platform-level permissions (TaskLina team)
  const hasPlatformRole = (roles) => {
    if (!currentUser) return false;
    return roles.includes(currentUser.platformRole);
  };

  // Check if user has organization-level permissions
  const hasOrgRole = (roles) => {
    if (!currentUser) return false;
    return roles.includes(currentUser.orgRole);
  };

  // Check if user is enterprise user (has organization access)
  const isEnterpriseUser = () => {
    if (!currentUser) return false;
    
    console.log('AuthContext.isEnterpriseUser: Checking user:', {
      email: currentUser.email,
      accountType: currentUser.accountType,
      organizationId: currentUser.organizationId,
      organizations: currentUser.organizations,
      orgRole: currentUser.orgRole
    });
    
    // Direct organization account type
    if (currentUser.accountType === 'organization') {
      console.log('AuthContext.isEnterpriseUser: Has organization accountType');
      return true;
    }
    
    // Has organization ID (direct member)
    if (currentUser.organizationId) {
      console.log('AuthContext.isEnterpriseUser: Has organizationId');
      return true;
    }
    
    // Has organizations array (multiple org memberships)
    if (currentUser.organizations && currentUser.organizations.length > 0) {
      console.log('AuthContext.isEnterpriseUser: Has organizations array');
      return true;
    }
    
    // Has organization role
    if (currentUser.orgRole) {
      console.log('AuthContext.isEnterpriseUser: Has orgRole');
      return true;
    }
    
    console.log('AuthContext.isEnterpriseUser: Not an enterprise user');
    return false;
  };

  // Check if user is individual user
  const isIndividualUser = () => {
    return !isEnterpriseUser();
  };

  // Get user's organization context
  const getOrganizationContext = () => {
    if (!currentUser) return null;
    
    return {
      organizationId: currentUser.organizationId,
      organizations: currentUser.organizations || [],
      orgRole: currentUser.orgRole,
      departmentId: currentUser.departmentId,
      isOrgAdmin: currentUser.isOrgAdmin || currentUser.orgRole === 'admin',
      isDeptManager: currentUser.isDeptManager || currentUser.orgRole === 'dept_manager'
    };
  };

  // Get user's effective role information
  const getUserRoles = () => {
    if (!currentUser) return {};
    
    return {
      // New enterprise roles (PRIMARY)
      platformRole: currentUser.platformRole,
      orgRole: currentUser.orgRole,
      
      // Account context
      accountType: currentUser.accountType,
      organizationId: currentUser.organizationId,
      departmentId: currentUser.departmentId,
      
      // Convenience flags - using correct platformRole values
      isPlatformAdmin: ['platform_admin', 'super_admin'].includes(currentUser.platformRole),
      isSuperAdmin: currentUser.platformRole === 'super_admin',
      isOrgAdmin: currentUser.orgRole === 'org_admin',
      isDeptManager: currentUser.orgRole === 'dept_manager' || currentUser.isDeptManager
    };
  };

  // Update the user profile
  const updateProfile = async (userData) => {
    const updatedUser = await authService.updateProfile(userData);
    setCurrentUser(updatedUser);
    return updatedUser;
  };

  // Update current user directly (for cases like profile picture upload)
  const updateCurrentUser = (userData) => {
    setCurrentUser(prev => ({
      ...prev,
      ...userData
    }));
  };

  // Refresh current user from server (useful after accepting invitations, role changes, etc.)
  const refreshUser = async () => {
    try {
      console.log('AuthContext: Refreshing user data from server...');
      const response = await authService.getCurrentUser();
      const userData = response.data || response;
      
      console.log('AuthContext: User refreshed:', {
        email: userData.email,
        organizationId: userData.organizationId,
        accountType: userData.accountType,
        orgRole: userData.orgRole
      });
      
      setCurrentUser(userData);
      
      // Check for account status (deactivation info)
      if (response.accountStatus?.isDeactivated) {
        setAccountStatus(response.accountStatus);
      }
      
      return userData;
    } catch (error) {
      console.error('AuthContext: Error refreshing user:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    login,
    socialLogin,
    register,
    sendVerificationCode,
    verifyAndRegister,
    resendVerificationCode,
    logout,
    hasRole,
    hasPlatformRole,
    hasOrgRole,
    isEnterpriseUser,
    isIndividualUser,
    getUserRoles,
    getOrganizationContext,
    updateProfile,
    updateCurrentUser,
    refreshUser,
    setCurrentUser,
    setToken,
    setAccountStatus,
    isAuthenticated: !!currentUser,
    accountStatus,
    isDeactivated: accountStatus?.isDeactivated || false,
    token, // Add token to the context value for socket authentication
  };

  console.log('AuthProvider value:', { currentUser, loading, isAuthenticated: !!currentUser, isDeactivated: accountStatus?.isDeactivated, token: token ? 'Present' : 'null' });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
